import React, { useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import OurExpertise from "../../components/home/OurExpertise";
import OurPartners from "../../components/home/OurPartners";
import {
  Sparkles,
  MapPin,
  Plane,
  Train,
  Users,
  ShieldCheck,
  Binoculars,
  Landmark,
  Route,
  ChevronRight,
  History,
  Award,
} from "lucide-react";
import { Link } from "react-router-dom";

import img1 from "../../Assets/img/1.jpg";
import img2 from "../../Assets/img/2.jpg";
import img3 from "../../Assets/img/3.jpg";
import img4 from "../../Assets/img/4.jpg";
import img5 from "../../Assets/img/5.jpg";
import img7 from "../../Assets/img/7.jpg";
import admin from "../../Assets/img/a2.jpeg";
import admin1 from "../../Assets/img/a3.jpeg";
import certificateImg from "../../Assets/certificate/image.png";

const stats = [
  { label: "Years Experience", value: "10+", icon: History },
  { label: "Expert Guides", value: "25+", icon: Users },
  { icon: Award, label: "Happy Travelers", value: "5K+" },
  { icon: MapPin, label: "Destinations", value: "50+" },
];

const tourThemes = [
  {
    icon: Binoculars,
    title: "Wildlife Expeditions",
    places: "Panna Tiger Reserve, Satpura, Bandhavgarh, Kanha, Pench, Kuno and Chambal Sanctuary.",
    color: "bg-emerald-50 text-emerald-700",
  },
  {
    icon: Sparkles,
    title: "Spiritual Journeys",
    places: "Ujjain, Omkareshwar, Varanasi, Chitrakoot, Ayodhya, Bodhgaya, and Amarkantak.",
    color: "bg-orange-50 text-orange-700",
  },
  {
    icon: Landmark,
    title: "Heritage & Architecture",
    places: "Khajuraho, Gwalior, Orchha, Sanchi, Bhimbetka, Mandu, and Ajanta Ellora caves.",
    color: "bg-stone-50 text-stone-700",
  },
  {
    icon: Route,
    title: "Tribal & Nature Trails",
    places: "Pachmarhi, Bhedaghat, Gond & Bhil Tribe regions, Jhabua, and Madhai wilderness.",
    color: "bg-blue-50 text-blue-700",
  },
];

const excursions = [
  "Raneh Water Fall", "Panna National Park", "Night Safari at Panna", "Ajay Garh Fort",
  "Kalingar Fort", "Orchha Fort", "Alaha & Uddal Fort Mahoba", "Dubela Palace",
  "Jata Shankar Temple", "Bhim Kund", "Ram Van", "Panna Temple",
  "Choumukh Nath Temple", "Chitrakoot", "Maihar"
];

const About = () => {
  const { scrollYProgress } = useScroll();
  const yRange = useTransform(scrollYProgress, [0, 1], [0, -50]);
  const [showMore, setShowMore] = React.useState(false);

  useEffect(() => {
    document.title = "About Us | Khajuraho Routes";
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="bg-[#FCFBFA] text-[#1A202C] font-sans selection:bg-orange-100 selection:text-orange-900">
      {/* --- HERO SECTION: Half Height & Theme Followed --- */}
      <section className="relative h-[50vh] flex items-center overflow-hidden bg-[#0A0A0A]">
        <motion.div style={{ y: yRange }} className="absolute inset-0 z-0">
          <img src={img1} className="w-full h-full object-cover opacity-60 scale-105" alt="Khajuraho" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
        </motion.div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1 }}
            className="max-w-4xl"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="h-[2px] w-12 bg-orange-main" />
              <span className="text-orange-main font-black tracking-[0.4em] text-[10px] uppercase">About us</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-black text-white leading-tight mb-6 uppercase tracking-tight">
              Pioneers of <span className="text-orange-main">Authentic</span> <br />
              Central India Tours
            </h1>
            <p className="text-base md:text-lg text-gray-300 max-w-2xl leading-relaxed mb-8 font-medium">
              We are curators of culture, heritage, and wilderness, dedicated to revealing the true soul of Khajuraho and beyond.
            </p>
          </motion.div>
        </div>
      </section>

      {/* --- STATS BAR --- */}
      <section className="relative z-20 -mt-10 max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-0.5 bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100">
          {stats.map((stat, i) => (
            <div key={i} className="p-8 flex flex-col items-center text-center group hover:bg-orange-50 transition-colors duration-500">
              <stat.icon className="text-orange-main mb-3 group-hover:scale-110 transition-transform" size={24} strokeWidth={2} />
              <span className="text-2xl font-black text-gray-900 mb-1">{stat.value}</span>
              <span className="text-[10px] uppercase tracking-widest text-gray-500 font-black">{stat.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* --- STORY SECTION --- */}
      <section className="py-10 lg:py-24 overflow-hidden bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            {/* Left Column: Premium Focal Image Stack */}
            <div className="relative h-auto lg:h-[700px] flex items-center justify-center mb-12 lg:mb-0">
              {/* Deep Glow Background */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-orange-main/5 rounded-full blur-[120px] -z-10" />

              {/* Back Image 1: Top-Right Anchor (Hidden on Mobile) */}
              <motion.div
                initial={{ opacity: 0, x: 60, y: -60 }}
                whileInView={{ opacity: 1, x: 0, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1.2, ease: "easeOut" }}
                className="absolute top-0 right-0 w-[85%] h-[350px] z-10 hidden lg:block"
              >
                <div className="relative h-full w-full group">
                  <img src={img3} alt="Heritage Anchor" className="w-full h-full object-cover rounded-[4rem] shadow-2xl transition-all duration-700 group-hover:scale-[1.02]" />
                  <div className="absolute inset-0 bg-black/5 rounded-[4rem] group-hover:bg-transparent transition-all" />
                </div>
              </motion.div>

              {/* Back Image 2: Bottom-Left Anchor (Hidden on Mobile) */}
              <motion.div
                initial={{ opacity: 0, x: -60, y: 60 }}
                whileInView={{ opacity: 1, x: 0, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1.2, delay: 0.3, ease: "easeOut" }}
                className="absolute bottom-0 left-0 w-[85%] h-[350px] z-10 hidden lg:block"
              >
                <div className="relative h-full w-full group">
                  <img src={img4} alt="Nature Anchor" className="w-full h-full object-cover rounded-[4rem] shadow-2xl transition-all duration-700 group-hover:scale-[1.02]" />
                  <div className="absolute inset-0 bg-black/5 rounded-[4rem] group-hover:bg-transparent transition-all" />
                </div>
              </motion.div>

              {/* Image 3: Center Focal Highlight (Primary on Mobile) */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="relative z-20 w-[85%] lg:w-[65%] h-[320px] lg:h-[260px] shadow-2xl"
              >
                <div className="relative h-full w-full">
                  <img src={admin} alt="Center Highlight" className="w-full h-full object-cover rounded-[2rem] lg:rounded-[3rem] shadow-2xl" />
                  {/* Floating Icon Decoration */}
                  <motion.div
                    animate={{ y: [0, -10, 0] }}
                    transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }}
                    className="absolute -top-3 -right-3 lg:-top-6 lg:-right-6 w-12 h-12 lg:w-16 lg:h-16 bg-orange-main rounded-xl lg:rounded-3xl flex items-center justify-center text-white shadow-2xl"
                  >
                    <MapPin size={24} className="lg:hidden" />
                    <MapPin size={28} className="hidden lg:block" />
                  </motion.div>
                </div>
              </motion.div>
            </div>

            {/* Right Column: Refined Content Area */}
            <div className="space-y-10">
              <div className="relative pl-8 border-l-4 border-orange-main">
                <span className="text-orange-main font-black tracking-[0.3em] text-[18px] uppercase block mb-3">About Us</span>

              </div>

              <div className="space-y-7 text-gray-600 leading-relaxed text-[16px] font-medium">
                <p className="first-letter:text-5xl first-letter:font-black first-letter:text-orange-main first-letter:mr-3 first-letter:float-left">
                  We are Khajuraho Routes is a group of travel experts ground Handling who have extensive knowledge about Khajuraho and Madhya Pradesh destinations, collectively work towards Handling group and Individual tourist Monument in Panna, Bandhavgarh, Kanha, Pench, Satpura, Tadoba, Ujjain, Omkareshwar, Maheshwar, Mandu, Ajanta, Ellora, Pachamrhi, Bedhaghat, Sanchi, Bhimbetika, Burhanpur, Chanderi , Gwalior, Bhopal, Orchha, Jhansi, Varanasi, Bodhgaya & Lucknow.
                </p>

                {/* Conditional Content: Always visible on Desktop, Toggleable on Mobile */}
                <div className={`${showMore ? "block" : "hidden"} lg:block space-y-7`}>
                  <p>
                    With 10 Years of Experience, Passion, in Depth location research & Knowledge helped us to transform our dream into a reality called Khajuraho Routes. Khajuraho Routes - a traveler's window to Khajuraho has its head office in Khajuraho and Associate offices in Agra, Gwalior, Orchha, Varanasi, Bodhgaya, Bandhavgarh, and Jabalpur to provide personalized services at every step.
                  </p>
                  <div className="grid gap-6">
                    <p>
                      We are registered travel Management Company who provides Hotel Reservations, Car Rentals, guide services, MICE segment etc. for group tour and independent travel programs. We have well versed trained staff personnel who provides cultural & historical introduction of the state to our guests. We offer tailor made packages and special interest tours by being most sensitive to your needs and best match them to the travel products available. We also provide you flexible options to suit your requirements which include complete hotel bookings, confirmed air bookings and complete transportation services.
                    </p>
                    <p>
                      Transportation We are providing service in the Transportation Sector. We provide all types of Cars, Taxis, Mini coaches, large coaches and all types of easy, smooth and comfortable transportation facilities to the guests' at the most nominal and right rates possible. All types of A/C and Non A/c comfortable transportation available at all times.
                    </p>
                  </div>
                </div>

                {/* Toggle Button: Hidden on Desktop */}
                <button
                  onClick={() => setShowMore(!showMore)}
                  className="lg:hidden flex items-center gap-2 text-orange-main font-black uppercase tracking-widest text-xs hover:text-orange-light transition-colors group"
                >
                  {showMore ? "See Less" : "Read More"}
                  <ChevronRight size={14} className={`transition-transform ${showMore ? "rotate-90" : "group-hover:translate-x-1"}`} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
<OurExpertise />

      {/* --- CERTIFICATION --- */}
      <section className="py-20 bg-[#fff7ed] overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="lg:col-span-5"
            >
              {/* <span className="text-orange-main font-black tracking-[0.3em] text-[11px] uppercase">Certified & Trusted</span> */}
              <h2 className="mt-3 text-2xl md:text-4xl font-black text-gray-900 uppercase tracking-tight leading-tight">
                Certified & Trusted
              </h2>
              <p className="mt-6 text-gray-600 text-base leading-relaxed font-medium">
                Our certificate reflects the credibility and professional standards behind Khajuraho Routes. We present it proudly so every traveler can plan with confidence.
              </p>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="rounded-2xl bg-white p-5 border border-orange-main/10 shadow-sm">
                  <ShieldCheck className="text-orange-main mb-3" size={26} />
                  <h3 className="font-black text-gray-900 mb-1">Verified Identity</h3>
                  <p className="text-sm text-gray-500 font-medium leading-relaxed">Transparent credentials for safer travel decisions.</p>
                </div>
                <div className="rounded-2xl bg-white p-5 border border-orange-main/10 shadow-sm">
                  <Award className="text-orange-main mb-3" size={26} />
                  <h3 className="font-black text-gray-900 mb-1">Professional Standards</h3>
                  <p className="text-sm text-gray-500 font-medium leading-relaxed">Reliable service values across tours and transport.</p>
                </div>
              </div>

              <Link
                to="/contact"
                className="mt-8 inline-flex items-center justify-center rounded-full bg-orange-main px-7 py-3.5 text-xs font-black uppercase tracking-widest text-white shadow-lg shadow-orange-main/20 transition-colors hover:bg-orange-light"
              >
                Contact Our Team
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="lg:col-span-7"
            >
              <div className="relative mx-auto max-w-3xl">
                <div className="absolute -inset-4 rounded-[2rem] bg-orange-main/10 blur-2xl" />
                <div className="relative rounded-[2rem] shadow-2xl border border-orange-main/10">
                  <div className="rounded-[1.5rem] overflow-hidden ">
                    <img
                      src={certificateImg}
                      alt="Khajuraho Routes certificate"
                      className="w-full h-auto object-contain"
                    />
                  </div>
                 
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* --- SPECIALTIES --- */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <span className="text-orange-main font-black tracking-widest text-[10px] uppercase">Specializations</span>
            <h2 className="text-3xl md:text-5xl font-black mt-3 uppercase tracking-tighter">The Pillars of Our Tourism</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {tourThemes.map((theme, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className={`p-8 rounded-3xl border border-gray-100 flex gap-6 group hover:shadow-2xl transition-all duration-500 bg-white shadow-sm`}
              >
                <div className={`w-14 h-14 shrink-0 rounded-2xl flex items-center justify-center ${theme.color} group-hover:scale-110 transition-transform shadow-sm`}>
                  <theme.icon size={24} strokeWidth={2} />
                </div>
                <div>
                  <h3 className="text-xl font-black text-gray-900 mb-2 uppercase tracking-tight">{theme.title}</h3>
                  <p className="text-gray-500 font-bold text-[13px] leading-relaxed">{theme.places}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* --- JOURNEY EXCURSIONS --- */}
      <section className="py-20 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <span className="text-orange-main font-black tracking-[0.3em] text-[12px] uppercase block mb-2">The Journey Path</span>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 uppercase tracking-tighter">
              Sequential <span className="text-orange-main">Excursions</span>
            </h2>
          </div>

          <div className="relative pt-8">
            {/* Background Path Lines for Desktop (Adjusted for 240px height) */}
            <div className="absolute top-[150px] left-0 w-full h-[2px] border-t-2 border-dashed border-orange-main/20 hidden lg:block -z-10" />
            <div className="absolute top-[455px] left-0 w-full h-[2px] border-t-2 border-dashed border-orange-main/20 hidden lg:block -z-10" />
            <div className="absolute top-[760px] left-0 w-full h-[2px] border-t-2 border-dashed border-orange-main/20 hidden lg:block -z-10" />

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-y-12 md:gap-y-16 gap-x-4 md:gap-x-8 relative z-10">
              {excursions.map((ex, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="relative group cursor-default"
                >
                  {/* Responsive Image Card with Overlay Text */}
                  <div className="relative w-full h-[180px] md:h-[200px] lg:h-[240px] rounded-[2rem] md:rounded-[2.5rem] overflow-hidden shadow-lg border-2 md:border-4 border-white group-hover:border-orange-main group-hover:shadow-xl transition-all duration-500">
                    <img
                      src={[img3, img4, img1, img5, img7, img2][i % 6]}
                      alt={ex}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
                    
                    {/* Card Content */}
                    <div className="absolute inset-0 p-4 md:p-6 flex flex-col justify-between">
                      {/* Step Number Badge */}
                      <div className="bg-orange-main text-white text-[9px] md:text-[10px] font-black w-6 h-6 md:w-8 md:h-8 flex items-center justify-center rounded-xl md:rounded-2xl shadow-lg transform -rotate-12 group-hover:rotate-0 transition-transform">
                        {String(i + 1).padStart(2, '0')}
                      </div>
                      
                      {/* Destination Name Overlay - Increased for Desktop */}
                      <h3 className="text-white text-[10px] md:text-[11px] lg:text-[14px] font-black uppercase tracking-widest leading-tight mb-1 group-hover:text-orange-main transition-colors">
                        {ex}
                      </h3>
                    </div>
                  </div>

                  {/* Connecting Arrow for Desktop */}
                  {i % 5 !== 4 && i < excursions.length - 1 && (
                    <div className="absolute top-[105px] md:top-[115px] lg:top-[110px] -right-3 md:-right-5 lg:-right-7 text-orange-main z-20 hidden lg:block bg-white rounded-full p-1 shadow-md border border-orange-main/10 group-hover:scale-110 transition-transform">
                      <ChevronRight size={16} strokeWidth={3} />
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* --- CONNECTIVITY --- */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-16 items-center">
          <div className="relative">
            <img src={admin1} alt="Connect" className="rounded-3xl shadow-2xl relative z-10 w-full object-cover h-[400px]" />
          </div>
          <div className="space-y-10">
            <div>
              <span className="text-orange-main font-black tracking-widest text-[10px] uppercase">Plan Your Arrival</span>
              <h2 className="text-3xl md:text-4xl font-black mt-3 mb-8 leading-tight uppercase tracking-tight">Effortless Connectivity</h2>
            </div>
            <div className="space-y-6">
              <div className="flex gap-6 p-6 bg-white rounded-2xl border border-gray-100 shadow-sm">
                <div className="w-12 h-12 rounded-xl bg-orange-main/10 flex items-center justify-center text-orange-main shrink-0"><Plane size={24} /></div>
                <div>
                  <h4 className="text-sm font-black mb-1 uppercase tracking-wider">Air Transit</h4>
                  <p className="text-gray-500 font-bold text-xs leading-relaxed">Direct flight services connect Khajuraho with New Delhi and Varanasi, offering the fastest gateway.</p>
                </div>
              </div>
              <div className="flex gap-6 p-6 bg-white rounded-2xl border border-gray-100 shadow-sm">
                <div className="w-12 h-12 rounded-xl bg-teal-main/10 flex items-center justify-center text-teal-main shrink-0"><Train size={24} /></div>
                <div>
                  <h4 className="text-sm font-black mb-1 uppercase tracking-wider">Rail Corridors</h4>
                  <p className="text-gray-500 font-bold text-xs leading-relaxed">Direct links to Delhi, Jhansi, and Satna. Convenient for travelers from Kolkata and Mumbai.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <OurPartners />
    </main>
  );
};

export default About;
